import { getCurrencySymbol } from '@angular/common';
import { BestFare, BestFareResult } from '@core/bff/types';
import { DateRangePickerComponent } from '@dynamic-components/booker-v2/shared/date-range-picker/date-range-picker.component';
import { addDays, compareAsc, getDaysInMonth, subDays } from 'date-fns';
import {
  JbDayAvailability,
  JbMonthVisibilityInterface,
} from 'jb-component-library';
import moize from 'moize';
import { isEmpty, range } from 'ramda';

export function createJBMonthObjForCL(
  date: Date,
  fareResponse: BestFareResult,
  currencySymbol: string,
  overlayMessage: string,
  isScheduledEnabled: boolean,
  isPricingEnabled: boolean,
  fareType?: string,
) {
  let inboundArr: JbDayAvailability[];
  const month = date.getMonth();
  const year = date.getFullYear();

  const outboundArr = reduceFaresResponseToMonthObj(
    month,
    year,
    fareResponse.outbound,
    currencySymbol,
    isScheduledEnabled,
    isPricingEnabled,
    fareType,
  );

  if (fareResponse.inbound) {
    inboundArr = reduceFaresResponseToMonthObj(
      month,
      year,
      fareResponse.inbound,
      currencySymbol,
      isScheduledEnabled,
      isPricingEnabled,
      fareType,
    );
  }
  const monthObj = {
    month: new Date(year, month, 1),
    monthAvailability: {
      days: outboundArr,
      overlayMessage: isEmpty(outboundArr) ? overlayMessage : '',
      isMonthUnavailableOverlay: isEmpty(outboundArr) ? true : false,
    },
    monthAvailabilityReturn: {
      days: inboundArr,
      overlayMessage: isEmpty(inboundArr) ? overlayMessage : '',
      isMonthUnavailableOverlay: isEmpty(inboundArr) ? true : false,
    },
  };

  return monthObj;
}
export function createJBMonthObjRoundTrip(
  targetMonth: Date,
  fareResponse: BestFareResult[],
  overlayMessage: string,
  isScheduledEnabled: boolean,
  isPricingEnabled: boolean,
  tripLength: number,
  departDateFare: BestFare | null,
  fareType?: string,
) {
  let inboundArr: JbDayAvailability[];
  const month = targetMonth.getMonth();
  const year = targetMonth.getFullYear();

  const currencySymbol = getCurrencySymbol(
    fareResponse[0].currencyCode,
    'narrow',
  );

  const outboundArr = reduceFaresResponseRoundTrip(
    targetMonth,
    fareResponse,
    'outbound',
    currencySymbol,
    isScheduledEnabled,
    isPricingEnabled,
    tripLength,
    fareType,
  );

  if (!departDateFare) {
    inboundArr = reduceFaresResponseRoundTrip(
      targetMonth,
      fareResponse,
      'inbound',
      currencySymbol,
      isScheduledEnabled,
      isPricingEnabled,
      tripLength,
      fareType,
    );
  } else {
    // get targetMonth
    const targetMonthIndex = fareResponse.findIndex(
      fare =>
        new Date(fare['inbound'][0].date).getMonth() === targetMonth.getMonth(),
    );
    inboundArr = reduceFaresAndCombineWithFare(
      departDateFare,
      targetMonth,
      fareResponse[targetMonthIndex]['inbound'],
      currencySymbol,
      isScheduledEnabled,
      isPricingEnabled,
      fareType,
    );
  }

  const monthObj = {
    month: new Date(year, month, 1),
    monthAvailability: {
      days: outboundArr,
      overlayMessage: isEmpty(outboundArr) ? overlayMessage : '',
      isMonthUnavailableOverlay: isEmpty(outboundArr) ? true : false,
    },
    monthAvailabilityReturn: {
      days: inboundArr,
      overlayMessage: isEmpty(inboundArr) ? overlayMessage : '',
      isMonthUnavailableOverlay: isEmpty(inboundArr) ? true : false,
    },
  };

  return monthObj;
}

export const generateMonthInDays = moize(
  (month: number, year: number): Date[] => {
    const selectedMonth = new Date(year, month);

    return range(1, getDaysInMonth(selectedMonth) + 1)
      .map(dateOfMonth => new Date(year, month, dateOfMonth))
      .map(date => date);
  },
);

export function enableAllDates(
  month: number,
  year: number,
  componentRef: DateRangePickerComponent,
  loadingText?: string,
) {
  const calendarDays = generateMonthInDays(month, year);
  const allDaysAvailMonthObj = createMonthObjNoPrices(
    month,
    year,
    calendarDays,
    loadingText,
  );
  componentRef.setMonthData(allDaysAvailMonthObj);
}

export function createMonthObjNoPrices(
  month: number,
  year: number,
  calendarMonthDays: Date[],
  loadingText?: string,
): JbMonthVisibilityInterface {
  const daysArr = calendarMonthDays.map(day => {
    const dayObj = {
      date: day,
      isAvailable: true,
      displayText: loadingText,
    };
    return dayObj;
  });

  const monthObj = {
    month: new Date(year, month, 1),
    monthAvailability: {
      days: daysArr,
      isMonthUnavailableOverlay: false,
    },
    monthAvailabilityReturn: {
      days: daysArr,
      isMonthUnavailableOverlay: false,
    },
  };
  return monthObj;
}

export const kFormatter = (num: number) => {
  return Math.abs(num) > 999
    ? Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1)) + 'k'
    : Math.sign(num) * Math.abs(num);
};

export function reduceFaresResponseToMonthObj(
  selectedMonth: number,
  selectedYear: number,
  monthArr: BestFare[],
  currencySymbol: string,
  isShowScheduleEnabled: boolean,
  isPricingEnabled: boolean,
  fareType?: string,
): JbDayAvailability[] {
  let datesInMonth: Date[] = [];
  if (!isShowScheduleEnabled) {
    datesInMonth = generateMonthInDays(selectedMonth, selectedYear);
  } else {
    datesInMonth = monthArr.map(fare => fare.date);
  }

  return datesInMonth.map(date => {
    let dayObj: {
      date: Date;
      isAvailable: boolean;
      displayText: string | null;
    };
    const fareFromRes = monthArr.find(
      fare => fare.date.getTime() === date.getTime(),
    );

    if (!!fareFromRes) {
      const isAvailable = isFareAvailable(fareFromRes.seats);
      let displayText = null;
      if (isAvailable && isPricingEnabled) {
        const flightPrice =
          fareType === 'usd'
            ? currencySymbol + fareFromRes.amount
            : kFormatter(Number(fareFromRes.amount));
        displayText = generateTemplateString(
          flightPrice,
          fareFromRes.isLowestFare,
        );
      }
      dayObj = {
        date,
        isAvailable: !isShowScheduleEnabled ? true : isAvailable,
        displayText,
      };
    } else {
      dayObj = {
        date,
        isAvailable: true,
        displayText: null,
      };
    }
    return dayObj;
  });
}

/** Reduce Best fares object and combine the price with the provided fare1 */
export function reduceFaresAndCombineWithFare(
  fare1: BestFare,
  targetMonth: Date,
  resultsArr: BestFare[],
  currencySymbol: string,
  isShowScheduleEnabled: boolean,
  isPricingEnabled: boolean,
  fareType?: string,
): JbDayAvailability[] {
  let datesInMonth: Date[] = [];
  if (!isShowScheduleEnabled) {
    datesInMonth = generateMonthInDays(
      targetMonth.getMonth(),
      targetMonth.getFullYear(),
    );
  } else {
    datesInMonth = resultsArr.map(fare => fare.date);
  }

  return datesInMonth.map(date => {
    let dayObj: {
      date: Date;
      isAvailable: boolean;
      displayText: string | null;
    };

    const fare2 = resultsArr.find(
      fare => fare.date.getTime() === date.getTime(),
    );

    if (fare1 && fare2) {
      const isAvailable =
        isFareAvailable(fare1.seats) && isFareAvailable(fare2.seats);
      let displayText = null;
      if (isAvailable && isPricingEnabled) {
        const flightPrice =
          fareType === 'usd'
            ? currencySymbol + (fare1.amount + fare2.amount)
            : kFormatter(Number(fare1.amount));
        displayText = generateTemplateString(
          flightPrice,
          false, // TODO: lowest fare round trip to be handled in another ticket
        );
      }
      dayObj = {
        date,
        isAvailable: !isShowScheduleEnabled ? true : isAvailable,
        displayText,
      };
    } else {
      dayObj = {
        date,
        isAvailable: true,
        displayText: null,
      };
    }
    return dayObj;
  });
}

export function reduceFaresResponseRoundTrip(
  targetMonth: Date,
  resultsArr: BestFareResult[],
  direction: 'inbound' | 'outbound',
  currencySymbol: string,
  isShowScheduleEnabled: boolean,
  isPricingEnabled: boolean,
  tripLength: number,
  fareType?: string,
): JbDayAvailability[] {
  // get targetMonth index from month arr
  const targetMonthIndex = resultsArr.findIndex(
    fare =>
      new Date(fare[direction][0]?.date)?.getMonth() ===
      targetMonth?.getMonth(),
  );

  // get opposite direction for fare2
  const oppositeDirection = direction === 'outbound' ? 'inbound' : 'outbound';

  let datesInMonth: Date[] = [];
  if (!isShowScheduleEnabled) {
    datesInMonth = generateMonthInDays(
      targetMonth.getMonth(),
      targetMonth.getFullYear(),
    );
  } else if (resultsArr[targetMonthIndex]) {
    datesInMonth = resultsArr[targetMonthIndex][direction].map(
      fare => fare.date,
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // reset the hours to midnight;

  return datesInMonth.map(date => {
    let dayObj: {
      date: Date;
      isAvailable: boolean;
      displayText: string | null;
    };

    // copy date and add trip length
    const fare2Date =
      direction === 'outbound'
        ? addDays(date, tripLength)
        : subDays(date, tripLength);

    // get the month index for the 2nd fare
    const fare2MonthIndex = resultsArr.findIndex(
      month =>
        month[oppositeDirection][0]?.date?.getMonth() === fare2Date?.getMonth(),
    );

    let fare1: BestFare;
    let fare2: BestFare;

    // first fare for first direction
    // date can't be less than today
    if (compareAsc(date, today) !== -1 && resultsArr[targetMonthIndex]) {
      fare1 = resultsArr[targetMonthIndex][direction].find(
        fare => fare.date.getTime() === date.getTime(),
      );
    }

    // get fare2 for the opposite direction
    // fare2Day can't be less than today
    if (compareAsc(fare2Date, today) !== -1 && resultsArr[fare2MonthIndex]) {
      fare2 = resultsArr[fare2MonthIndex][oppositeDirection].find(
        fare => fare.date.getTime() === fare2Date.getTime(),
      );
    }

    if (fare1 && fare2) {
      const isAvailable =
        isFareAvailable(fare1.seats) && isFareAvailable(fare2.seats);
      let displayText = null;
      if (isAvailable && isPricingEnabled) {
        const flightPrice =
          fareType === 'usd'
            ? currencySymbol + (fare1.amount + fare2.amount)
            : kFormatter(Number(fare1.amount));
        displayText = generateTemplateString(
          flightPrice,
          false, // TODO: lowest fare round trip to be handled in another ticket
        );
      }
      dayObj = {
        date,
        isAvailable: !isShowScheduleEnabled ? true : isAvailable,
        displayText,
      };
    } else {
      dayObj = {
        date,
        isAvailable: true,
        displayText: null,
      };
    }
    return dayObj;
  });
}

export const setOverlayMessageForMonth = (
  componentRef: DateRangePickerComponent,
  year: number,
  month: number,
  overlayMessageDepart: string,
  overlayMessageReturn: string,
): void => {
  componentRef.setMonthData({
    month: new Date(year, month, 1),
    monthAvailability: {
      overlayMessage: overlayMessageDepart,
      days: [],
      isMonthUnavailableOverlay: true,
    },
    monthAvailabilityReturn: {
      overlayMessage: overlayMessageReturn,
      days: [],
      isMonthUnavailableOverlay: true,
    },
  });
};

export const isFareAvailable = (seatNumber: number): boolean =>
  seatNumber > 0 ? true : false;

export const generateTemplateString = (
  displayText: any,
  isLowestFare: boolean,
) => `<span class="${isLowestFare ? 'b' : ''}">${displayText}</span>`;
